import {
  AlertTooltip,
  Button,
  IconCheckOutline,
  IconLoadingSpinner,
  IconRefresh,
  NumberField,
  Typography,
  bodySmallTypography,
  boldBodySmallTypography,
  neutral20,
  success60,
} from '@leaftrade/leaftrade-component-library';
import PropTypes from 'prop-types';
import React from 'react';
import useQuickOrderQuantityCell from '../../hooks/QuickOrder/useQuickOrderQuantityCell';
import useQuickOrderQuantityNavigation from '../../hooks/QuickOrder/useQuickOrderQuantityNavigation';
import { useQuickOrderTableNavigationContext } from './QuickOrderTableNavigationContext';
import {
  QuickOrderAllQuantityCellWrapper,
  QuickOrderQuantityCellWrapper,
  QuickOrderRecheckWrapper,
} from './QuickOrderTable.styles';

const QuickOrderQuantityCell = ({
  isAllLocations = false,
  item,
  itemAlert: itemAlertProp,
  location,
  quantityCellStateRef,
  rowIndex,
}) => {
  const {
    alertIsError,
    alertIsStockIssue,
    batchQueuesEnabled,
    handleQuickOrderRefresh,
    handleQuantityBlur,
    handleQuantityBlurCapture,
    handleQuantityChange,
    handleQuantityFocusCapture,
    handleQuantityKeyDown,
    isQuantityPending,
    isRefreshing,
    itemAlert,
    quantityCellColumnId,
    quantityCellRowId,
    quantityInputRef,
    quantityValue,
    setTooltipOpen,
    showSuccessAdornment,
    tooltipOpen,
  } = useQuickOrderQuantityCell({
    isAllLocations,
    item,
    itemAlert: itemAlertProp,
    location,
    quantityCellStateRef,
  });
  const { orderedRowIds, virtualizedRowScrollRef } =
    useQuickOrderTableNavigationContext();
  const quantityNavigationProps = useQuickOrderQuantityNavigation({
    columnId: quantityCellColumnId,
    orderedRowIds,
    rowId: quantityCellRowId,
    rowIndex,
    virtualizedRowScrollRef,
  });
  const endAdornment = isQuantityPending ? (
    <IconLoadingSpinner data-testid='quick-order-quantity-cell--loading-icon' />
  ) : (
    showSuccessAdornment && (
      <IconCheckOutline
        data-testid='quick-order-quantity-cell--success-icon'
        fill={success60.hex}
      />
    )
  );

  const CellWrapper = isAllLocations
    ? QuickOrderAllQuantityCellWrapper
    : QuickOrderQuantityCellWrapper;

  if (isRefreshing) {
    return <IconLoadingSpinner />;
  }

  return (
    <CellWrapper
      data-testid='quick-order-quantity-cell--component--div'
      onBlurCapture={handleQuantityBlurCapture}
      onFocusCapture={handleQuantityFocusCapture}
      {...quantityNavigationProps}
    >
      <NumberField
        allowNegative={false}
        decimalScale={0}
        disabled={isQuantityPending}
        onBlur={handleQuantityBlur}
        endAdornment={endAdornment}
        onChange={handleQuantityChange}
        onKeyDown={handleQuantityKeyDown}
        placeholder='0'
        startAdornment={
          itemAlert && (
            <AlertTooltip
              alertType={itemAlert.level}
              clickable
              id={`${item.cartItemId}-${location?.id || 'all'}`}
              isOpen={tooltipOpen}
              setIsOpen={setTooltipOpen}
              tooltipPlacement='left'
            >
              <>
                {typeof itemAlert === 'string' ? (
                  <Typography bodyStyle={bodySmallTypography} gutterBottom>
                    {itemAlert}
                  </Typography>
                ) : (
                  <>
                    <Typography bodyStyle={bodySmallTypography} gutterBottom>
                      {itemAlert.title || itemAlert.message}
                    </Typography>
                    <Typography
                      bodyStyle={bodySmallTypography}
                      sx={{ color: neutral20.hex }}
                    >
                      {itemAlert.subtitle || itemAlert.sub_message}
                    </Typography>
                  </>
                )}
              </>
            </AlertTooltip>
          )
        }
        sx={{
          '& .form-control, & select, & input:not(.lcl-search-field__input)': {
            border: 'none !important',
          },
        }}
        testingId='quick-order-quantity-cell--number-field'
        ref={quantityInputRef}
        value={quantityValue}
      />
      {batchQueuesEnabled &&
        alertIsError &&
        alertIsStockIssue &&
        !isAllLocations && (
          <QuickOrderRecheckWrapper>
            <Button
              onClick={handleQuickOrderRefresh}
              size='xs'
              startIcon={<IconRefresh width='16px' height='16px' />}
              variant='secondary'
            >
              <Typography bodyStyle={boldBodySmallTypography} component='span'>
                Recheck
              </Typography>
            </Button>
          </QuickOrderRecheckWrapper>
        )}
    </CellWrapper>
  );
};

QuickOrderQuantityCell.propTypes = {
  isAllLocations: PropTypes.bool,
  rowIndex: PropTypes.number,
  item: PropTypes.shape({
    cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variants: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  itemAlert: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      level: PropTypes.string,
      message: PropTypes.string,
      sub_message: PropTypes.string,
      subtitle: PropTypes.string,
      title: PropTypes.string,
    }),
  ]),
  location: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  quantityCellStateRef: PropTypes.shape({
    current: PropTypes.shape({
      activeInputKey: PropTypes.string,
      drafts: PropTypes.shape({}),
    }),
  }).isRequired,
};

export default QuickOrderQuantityCell;
